import React, { useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { UserService } from '../services/UserService';
import * as yup from 'yup';
import { Formik } from 'formik';
import Loader from '../components/Loader';
import ErrorToast from '../components/ErrorToast';
import PublicLayout from '../components/PublicLayout';
import ButtonPrimary from '../components/Button/ButtonPrimary';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';

const ForgotPassword = () => {
  const { search } = useLocation();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const forgotPasswordMutation = useMutation(
    ({ email, turnstileToken }: { email: string; turnstileToken: string }) => {
      return UserService.forgotPassword(
        email,
        turnstileToken,
        searchParams.has('redirect_to')
          ? (searchParams.get('redirect_to') as string)
          : undefined,
      );
    },
    {
      onSuccess: async () => {
        setSuccess(true);
      },
      onError: () => {
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        ErrorToast(t('Errors.general'));
      },
    },
  );

  return (
    <PublicLayout>
      <>
        {forgotPasswordMutation.isLoading && <Loader />}

        {success ? (
          <div>
            <h2 className="text-2xl font-semibold mb-5">
              {t('ForgotPassword.success.headline')}
            </h2>

            <p className="text-sm font-normal mb-3">
              {t('ForgotPassword.success.firstParagraph')}
            </p>

            <p className="text-sm font-normal">
              {t('ForgotPassword.success.secondParagraph')}
            </p>

            <div className="mt-7">
              <ButtonPrimary href={`/login${search}`}>
                {t('ForgotPassword.success.backToLoginAction')}
              </ButtonPrimary>
            </div>
          </div>
        ) : (
          <>
            <div>
              <h2 className="text-2xl font-semibold mb-1.5">
                {t('ForgotPassword.headline')}
              </h2>

              <p className="text-sm font-normal">
                {t('ForgotPassword.subheadline')}
              </p>
            </div>

            <Formik
              enableReinitialize={true}
              initialValues={{
                email: '',
              }}
              validationSchema={yup.object({
                email: yup
                  .string()
                  .trim()
                  .lowercase()
                  .email(t('FormValidations.email'))
                  .required(t('FormValidations.emailRequired')),
              })}
              onSubmit={async (values, { setSubmitting }) => {
                if (!turnstileToken) {
                  ErrorToast(t('Errors.captchaRequired'));
                  setSubmitting(false);
                  return;
                }

                setSuccess(false);

                forgotPasswordMutation.mutate({
                  email: values.email,
                  turnstileToken: turnstileToken,
                });

                setSubmitting(false);
              }}
            >
              {({
                values,
                errors,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <form
                  className="space-y-7 text-sm text-black font-medium dark:text-white"
                  onSubmit={handleSubmit}
                >
                  <div>
                    <label htmlFor="email" className="">
                      {t('FormLabels.email')}{' '}
                    </label>
                    <div className="mt-2.5">
                      <input
                        id="email"
                        type="email"
                        placeholder="example@example.com"
                        className="w-full"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.email && (
                        <p className="text-red-500 text-xs italic mt-1">
                          {errors.email}
                        </p>
                      )}{' '}
                    </div>
                  </div>

                  <div>
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY!}
                      options={{ size: 'flexible' }}
                      onSuccess={setTurnstileToken}
                      onExpire={() => setTurnstileToken(null)}
                      onError={() => setTurnstileToken(null)}
                    />
                  </div>

                  <div>
                    <ButtonPrimary disabled={isSubmitting || !turnstileToken}>
                      {t('ForgotPassword.forgotPasswordAction')}
                    </ButtonPrimary>
                  </div>
                </form>
              )}
            </Formik>
          </>
        )}
      </>
    </PublicLayout>
  );
};

export default ForgotPassword;
